import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { usePrevious } from 'hooks/usePrevious';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';

import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import EditIcon from '@material-ui/icons/EditOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';

import compose from 'helpers/compose';
import { formatAmountValue, fractionToPercent } from 'helpers/helpers';
import { CalculationMode } from 'common/interfaces';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import RetirementIcon from 'components/Icons/RetirementIcon';

import { RootStore } from 'stores/rootStore';
import AdditionalTargets from '../components/AdditionalTargets';
import NewReserves from '../components/Reserves';
import { Params } from '../accountEditRoutes';
import { OverviewData, FinancesStore } from 'stores/workflowStores/finances/financesStore';
import { Currency, AccountMode, PersonType } from 'enums';

import useStyles from 'pages/Accounts/Edit/components/styles/moduleStyles';
import RentModals from 'pages/Accounts/Edit/components/Rent/RentModals';

interface Props {
  rootStore: RootStore;
  financesStore: FinancesStore;
  overview: OverviewData;
  currency: Currency;
  mode: AccountMode;
}

const Target: React.FC<Props> = ({ rootStore, financesStore, overview, currency, mode }) => {
  const { accountId } = useParams<Params>();

  const { isRentOpen, setIsRentOpen } = financesStore;
  const prevIsRentOpen = usePrevious(isRentOpen);
  const classes = useStyles();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  // FIXME: needed?
  useEffect(() => {
    if (prevIsRentOpen && !isRentOpen) {
      financesStore.fetchData();
    }
  }, [accountId, isRentOpen, financesStore, prevIsRentOpen]);

  const handleRentClose = () => {
    setIsRentOpen(false);
  };

  const displayPortfolioRent = () => {
    const { portfolioRentCalculationMode } = overview.rentData;
    const dataKey =
      portfolioRentCalculationMode === CalculationMode.MANUAL ? 'plannedPortfolioRent' : 'requiredPortfolioRent';

    if (overview.rentData && !(dataKey in overview.rentData)) {
      return;
    }
    return (
      <>
        <strong>{formatAmountValue(overview.rentData[dataKey].value, currency)}</strong>
        &nbsp;{t('general_frequency_MONTHLY')}
      </>
    );
  };

  const rentInfo = () => {
    return (
      <Grid container spacing={0} alignItems="stretch" className={classes.moduleWrapper}>
        <Grid container item alignContent="center" alignItems="center" className={`${classes.item} ${classes.target}`}>
          <Grid item xs={1} className={classes.icon}>
            <RetirementIcon fontSize="inherit" />
          </Grid>
          <Grid item xs={9} className={classes.info}>
            <Typography variant="h3" className={`${classes.title}`}>
              {`${t('finQuestionnary_pension_lifetimeRentPortfolio')}`}
            </Typography>
            <span>
              {t('general_since')}:&nbsp;
              <strong>{DateTime.fromSQL(overview.rentData.start).setLocale(language).toFormat('LLLL yyyy')}</strong>
            </span>
            <span>
              {t([
                `calculationMode_rent_allowance_${overview.rentData.portfolioRentCalculationMode}`,
                `calculationMode_rent_${overview.rentData.portfolioRentCalculationMode}`,
              ])}
            </span>
            <span>
              {overview.rentData.portfolioRentCalculationMode === CalculationMode.CALCULATED ? (
                <>{t('calculationMode_willBeCalculated')}</>
              ) : (
                displayPortfolioRent()
              )}
            </span>
            {overview.rentData.inflation.enabled && (
              <span>
                {t('general_inflationPlanned')}: {fractionToPercent(overview.rentData.inflation.rate)}
              </span>
            )}
          </Grid>
          <Grid item xs={2} className={classes.actions}>
            <IconButton
              size="medium"
              className="account-delete-button"
              onClick={() => {
                setIsRentOpen(true);
              }}
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
            <IconButton size="medium" className="accountEdit-button" onClick={() => setConfirmDialog(true)}>
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const [confirmDialog, setConfirmDialog] = useState(false);

  return overview ? (
    <>
      {overview.personType === PersonType.PHYSICAL && (
        <>
          <RentModals
            opened={isRentOpen}
            handleClose={handleRentClose}
            defaultInflation={overview.options.defaultInflationRate}
            defaultInflationType={overview.options.defaultInflationType}
            mode={mode}
            options={overview.targetData.options}
            accountId={accountId}
            personType={overview.personType}
            existingFlow={overview.rentData?.portfolioRentCalculationMode}
            refresh={() => financesStore.fetchData()}
          />
          <ConfirmDialog
            isOpen={confirmDialog}
            confirm={async () => {
              setConfirmDialog(false);
              await financesStore?.deleteRent();
            }}
            close={() => setConfirmDialog(false)}
          >
            {t('additionalTargets_confirmDeleteRent')}?
          </ConfirmDialog>

          <Grid container spacing={0}>
            <Grid item>
              <Typography variant="h3" style={{ paddingLeft: '8px' }}>
                {t('finQuestionnary_goalPlan_renIncome')}
              </Typography>
            </Grid>
            <Grid item style={{ marginLeft: '16px' }}>
              {!overview.rentData && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setIsRentOpen(true)}
                  startIcon={<AddIcon fontSize="medium" />}
                >
                  {t('add_rent')}
                </Button>
              )}
            </Grid>
          </Grid>
        </>
      )}
      {overview.rentData ? (
        rentInfo()
      ) : (
        <Grid container>
          <Grid item xs={12}>
            &nbsp;
          </Grid>
        </Grid>
      )}

      <NewReserves
        currency={currency}
        reservesData={overview.reserveData}
        accountId={accountId}
        fullWidth={true}
        refresh={financesStore.fetchData}
        openRent={overview.rentData !== undefined ? undefined : setIsRentOpen}
      />
      {mode !== AccountMode.LTIP && (
        <AdditionalTargets
          currency={currency}
          targetsData={overview.targetData}
          accountId={accountId}
          targetsOnly={true}
          refresh={financesStore.fetchData}
          defaultInflation={overview.options.defaultInflationRate}
          defaultInflationType={overview.options.defaultInflationType}
        />
      )}
    </>
  ) : null;
};

export default compose(inject('rootStore', 'financesStore'), observer)(Target);
